import { zustandStorage } from "@helpers/storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

// Same-device wall clock for content-only modules, whose report has no timed
// backend signal to sum. Keyed by session id so a same-device refresh shows the
// value the finish screen did; other devices fall back to the placeholder.
const MAX_TRACKED_SESSIONS = 20;

const canonicalKey = (id: number, numericId?: number): number =>
	numericId && numericId > 0 ? numericId : id;

type State = {
	elapsedBySession: Record<number, number>;
	trackedOrder: number[];
	saveSessionElapsed: (
		id: number,
		numericId: number | undefined,
		seconds: number,
	) => void;
	getSessionElapsed: (id: number, numericId?: number) => number | undefined;
};

export const useProethicaSessionTimeStore = create<State>()(
	persist(
		(set, get) => ({
			elapsedBySession: {},
			trackedOrder: [],
			saveSessionElapsed: (id, numericId, seconds) =>
				set((state) => {
					const key = canonicalKey(id, numericId);
					if (!key || seconds <= 0) {
						return state;
					}
					// A Record's integer keys iterate in ascending order, not insertion
					// order, so eviction tracks recency explicitly and drops the oldest
					// key once the cap is exceeded.
					const order = [...state.trackedOrder.filter((k) => k !== key), key];
					const elapsedBySession = {
						...state.elapsedBySession,
						[key]: seconds,
					};
					while (order.length > MAX_TRACKED_SESSIONS) {
						const evicted = order.shift();
						if (evicted != null) {
							delete elapsedBySession[evicted];
						}
					}
					return { elapsedBySession, trackedOrder: order };
				}),
			getSessionElapsed: (id, numericId) => {
				const { elapsedBySession } = get();
				return (
					elapsedBySession[canonicalKey(id, numericId)] ?? elapsedBySession[id]
				);
			},
		}),
		{
			name: "proethicaSessionTimeStore",
			storage: createJSONStorage(() => zustandStorage),
		},
	),
);
